import { getApiErrorCode, getApiErrorMessage } from '@/lib/utils/error.utils';

export interface ActionFailureProps {
  /** The failed mutation's error. Nothing renders when there is none. */
  error: unknown;
  /** Copy to show when the failure carries no reason of its own. */
  fallback: string;
  /**
   * Console-specific next step per Backend error code, rendered BESIDE the
   * API's own reason. The API says what went wrong; this says what the
   * operator can do about it.
   */
  hintsByCode?: Record<string, string>;
}

/**
 * The inline failure block for an admin console write action, promoted from
 * `features/admin/startups/components/StartupActionFailure.tsx` (mnr-
 * standards promotion rule: a domain-agnostic component used by a second
 * feature - the matching-run trigger dialog - moves to `common/` rather
 * than being duplicated).
 *
 * Every write that fires from a dialog which STAYS OPEN on failure belongs
 * inline beside the failing control with `role="alert"`, never as a toast
 * behind the overlay (ui-guidelines.md §12).
 *
 * `getApiErrorMessage`, never `getApiErrorDetail`: the latter returns
 * `undefined` for a raw Nest 404 or a class-validator 400, which is how a
 * failed delete once showed the operator an empty dialog. The per-code hint
 * is ADDITIONAL to that message and never replaces it - substituting
 * console copy for the API's reason throws away the only account of what
 * actually happened.
 */
export function ActionFailure({ error, fallback, hintsByCode }: ActionFailureProps) {
  if (error == null) return null;

  const code = getApiErrorCode(error);
  const hint = code ? hintsByCode?.[code] : undefined;

  return (
    <p role="alert" className="border-red/30 bg-red/10 text-red mt-4 rounded-lg border px-4 py-3 text-sm">
      {getApiErrorMessage(error, fallback)}
      {hint && <span className="mt-1 block">{hint}</span>}
    </p>
  );
}
